import { createMeasuredPlaybackEvidence } from "./measured-playback-evidence.mjs";
import { captureBriefCompletionActivityOwner, recordBriefCompletionActivity } from "@/shared/brief/completion-activity";
import { retainedAudioOwnerIsCurrent, type RetainedAudioOwner, type RetainedAudioRecording } from "./retained-audio-store";
import { recordCompletedBriefAudio, type BriefAudioCompletionContext, type BriefAudioShareExcerpt, type BriefAudioAcceptedStory } from "./brief-audio-completion-store";

/** A fresh full Audio delivery earns a day only after its exact recording is
 * committed and the measured media naturally finishes without skipped content. */
export function installFullBriefAudioCompletion(input: {
  player: HTMLAudioElement;
  owner: RetainedAudioOwner;
  isActive: () => boolean;
  language: string;
  context?: BriefAudioCompletionContext;
  excerpts?: BriefAudioShareExcerpt[];
  acceptedStories?: BriefAudioAcceptedStory[];
}) {
  const activityOwner = captureBriefCompletionActivityOwner();
  let evidence: ReturnType<typeof createMeasuredPlaybackEvidence> | null = null;
  let durationMs = 0;
  let measuredComplete = false;
  let completedAtEpochMs = 0;
  let recording: RetainedAudioRecording | null = null;
  let credited = false;
  let disposed = false;
  let contentSourceUrl = "";
  const current = () => !disposed && retainedAudioOwnerIsCurrent(input.owner) && activityOwner.scope === input.owner.scope;
  const contentActive = () => current() && input.isActive() && Boolean(contentSourceUrl) &&
    input.player.currentSrc === contentSourceUrl && !input.player.loop;
  function credit() {
    if (credited || !measuredComplete || !recording || recording.kind !== "brief" || !current() || recording.ownerScope !== input.owner.scope ||
      recording.language !== input.language || recording.expiresAtEpochMs <= Date.now() ||
      Math.abs(recording.durationMs - durationMs) > 1000) return;
    credited = recordBriefCompletionActivity({ owner: activityOwner, source: "audio",
      completionId: `audio:${recording.id}`, completedAtEpochMs });
    recordCompletedBriefAudio({ owner: input.owner, recording, completedAtEpochMs,
      context: input.context, excerpts: input.excerpts || [], acceptedStories: input.acceptedStories });
  }
  function observe() {
    if (!contentActive()) return;
    const measured = Math.round(input.player.duration * 1000);
    if (!evidence && Number.isFinite(measured) && measured > 0) { durationMs = measured; evidence = createMeasuredPlaybackEvidence(durationMs); }
    if (Number.isFinite(measured) && Math.abs(measured - durationMs) > 250) evidence?.discontinuity(durationMs);
    evidence?.observe(input.player.currentTime * 1000, performance.now(), input.player.playbackRate, !input.player.paused && !input.player.seeking);
  }
  const paused = () => evidence?.pause();
  const seeking = () => evidence?.discontinuity(input.player.currentTime * 1000);
  const ended = (event: Event) => {
    if (!contentActive()) return;
    measuredComplete = evidence?.ended(input.player.currentTime * 1000, performance.now(), input.player.playbackRate,
      event.isTrusted && input.player.ended) === true;
    if (measuredComplete) { completedAtEpochMs = Date.now(); credit(); }
  };
  const listeners: Array<[keyof HTMLMediaElementEventMap, EventListener]> = [
    ["timeupdate", observe], ["loadedmetadata", observe], ["playing", observe],
    ["pause", paused], ["waiting", paused], ["ratechange", paused], ["seeking", seeking], ["ended", ended],
  ];
  listeners.forEach(([event, handler]) => input.player.addEventListener(event, handler));
  const detach = () => listeners.forEach(([event, handler]) => input.player.removeEventListener(event, handler));
  return {
    armContent(sourceUrl: string) { contentSourceUrl = sourceUrl; },
    bindRecording(saved: Promise<RetainedAudioRecording | null>) {
      void saved.then((value) => { recording = value; credit(); }).catch(() => { /* Failed persistence never awards a day. */ });
    },
    // The ordinary ended handler removes media listeners immediately. Preserve
    // already-proven evidence while its exact IndexedDB commit settles.
    onDetached() { detach(); if (!measuredComplete) disposed = true; },
    dispose() { disposed = true; detach(); },
  };
}
